<template>
  <span
    :data-id="id"
    class="relative flex items-center flex-shrink-0 rounded-full"
    :class="{
      'h-7': !dense,
      'h-6': dense
    }"
    :title="currentChip.label"
    :style="{
      backgroundColor: currentChip.backgroundColor,
      padding: '0rem 0.5rem'
    }"
  >
    <BlTextBody3
      :data-id="id"
      :text="currentChip.label"
      class="truncate"
      :class="{
        'text-sm': !dense,
        'text-xs': dense
      }"
      :style="{ color: currentChip.color }"
    />
  </span>
</template>

<script>
import BlTextBody3 from "./BlTextBody3.vue";

export default {
  name: "BlChip",
  components: { BlTextBody3 },
  props: {
    id: { type: String, default: "bl-chip" },
    chip: { type: String, default: "chip" },
    color: {
      type: String,
      required: false,
      default: "cornflower" // cornflower
    },
    dense: { type: Boolean, default: false }
  },
  data() {
    const DARK_PURPLE_1 = "#564389";
    const LIGHT_PURPLE_1 = "#E7E0EC";
    const GREY = "#363636";
    const GREY_2 = "#E4E4E4";
    //
    const SLATE_8 = "#475467";
    const SLATE_3 = "#F2F4F7";
    const ROSE_7 = "#F63D68";
    const ROSE_2 = "#FFF1F3";
    const ORANGE_7 = "#FB6514";
    const ORANGE_2 = "#FFF6ED";
    const LIME_7 = "#789719";
    const LIME_2 = "#F2F8CF";
    const CORNFLOWER_8 = "#0172A3";
    const CORNFLOWER_2 = "#E0F4FE";
    const TULIP_8 = "#A66402";
    const TULIP_2 = "#FFFBC1";
    // const TULIP_7 = "#D18D00";
    // const TULIP_1 = "#FFFDE7";
    const GRASS_8 = "#457C0F";
    const GRASS_3 = "#D3F99D";
    const GRASS_2 = "#E9FCCB";
    const TOMATO_7 = "#DD3F30";
    const TOMATO_2 = "#FEE4E2";
    //
    return {
      colors: {
        cornflower: { color: CORNFLOWER_8, backgroundColor: CORNFLOWER_2 },
        grass: { color: GRASS_8, backgroundColor: GRASS_3 },
        lime: { color: LIME_7, backgroundColor: LIME_2 },
        purple: { color: DARK_PURPLE_1, backgroundColor: LIGHT_PURPLE_1 },
        rose: { color: ROSE_7, backgroundColor: ROSE_2 },
        slate: { color: SLATE_8, backgroundColor: SLATE_3 },
        tomato: { color: TOMATO_7, backgroundColor: TOMATO_2 },
        tulip: { color: TULIP_8, backgroundColor: TULIP_2 }
      },
      chips: {
        chip: { label: "Chip", color: GREY, backgroundColor: GREY_2 },
        // client status
        active: {
          label: "Active",
          color: LIME_7,
          backgroundColor: LIME_2
        },
        archived: {
          label: "Discharged",
          color: ROSE_7,
          backgroundColor: ROSE_2
        },
        at_risk_of_discharge: {
          label: "At risk of discharge",
          color: ORANGE_7,
          backgroundColor: ORANGE_2
        },
        // target status
        pending: {
          label: "Pending",
          color: SLATE_8,
          backgroundColor: SLATE_3
        },
        in_acquisition: {
          label: "In acquisition",
          color: TULIP_8,
          backgroundColor: TULIP_2
        },
        paused: {
          label: "Paused",
          color: DARK_PURPLE_1,
          backgroundColor: LIGHT_PURPLE_1
        },
        mastered: {
          label: "Mastered",
          color: GRASS_8,
          backgroundColor: GRASS_2
        },
        discontinued: {
          label: "Discontinued",
          color: TOMATO_7,
          backgroundColor: TOMATO_2
        },
        // session status
        draft: {
          label: "Draft",
          color: GREY,
          backgroundColor: GREY_2
        },
        // credit transaction status
        "CreditTransaction::Addition": {
          label: "Addition",
          color: "#FCFCFD",
          backgroundColor: "#98A2B3"
        },
        "CreditTransaction::Subtraction": {
          label: "Subtraction",
          color: "#667085",
          backgroundColor: "#F9FAFB"
        },
        // report status
        unpublished: {
          label: "Draft",
          color: DARK_PURPLE_1,
          backgroundColor: LIGHT_PURPLE_1
        },
        published: {
          label: "Published",
          color: GRASS_8,
          backgroundColor: GRASS_3
        },
        // appointment status
        scheduled: {
          label: "Scheduled",
          color: ORANGE_7,
          backgroundColor: ORANGE_2
        },
        cancelled: {
          label: "Cancelled",
          color: ROSE_7,
          backgroundColor: ROSE_2
        },
        in_progress: {
          label: "In progress",
          color: "#487ADF",
          backgroundColor: "#DFEAFA"
        },
        booking_in_progress: {
          label: "Booking in progress",
          color: CORNFLOWER_8,
          backgroundColor: CORNFLOWER_2
        },
        booking_in_review: {
          label: "Booking in review",
          color: CORNFLOWER_8,
          backgroundColor: CORNFLOWER_2
        },
        completed: {
          label: "Completed",
          color: LIME_7,
          backgroundColor: LIME_2
        },
        orphan: {
          label: "No therapist assigned",
          color: TOMATO_7,
          backgroundColor: TOMATO_2
        },
        // sales pipeline stage
        Admitted: {
          label: "Admitted",
          color: LIME_7,
          backgroundColor: LIME_2
        },
        Pending: {
          label: "Pending",
          color: TULIP_8,
          backgroundColor: TULIP_2
        },
        Discontinued: {
          label: "Discontinued",
          color: ROSE_7,
          backgroundColor: ROSE_2
        },
        // super admin - center status
        /**
         * active -> same as client active status
         */
        deactivated: {
          label: "Deactivated",
          color: DARK_PURPLE_1,
          backgroundColor: LIGHT_PURPLE_1
        },
        // super admin - subscription status
        /**
         * scheduled -> same as appointment scheduled status
         * active -> same as client active status
         */
        inactive: {
          label: "Inactive",
          color: SLATE_8,
          backgroundColor: SLATE_3
        }
      }
    };
  },
  computed: {
    currentChip() {
      const found = this.chips[this.chip];
      if (found) return found;
      const used = this.colors[this.color] || this.colors["cornflower"];
      return {
        label: this.chip,
        color: used.color,
        backgroundColor: used.backgroundColor
      };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/BlChip.vue